'use client';

import { useAuthV2 } from '@/hooks/useAuthV2';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useEffect } from 'react';

import {
  buildLaunchDarklyContext,
  identifyLaunchDarklyContext,
  isLaunchDarklyInitializationTimeout,
  subscribeToLaunchDarklyReady,
} from './launchdarkly-context.utils';

// Limpa entradas antigas do LaunchDarkly no localStorage
function cleanOldLDEntries() {
  if (typeof window === 'undefined') return;

  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith('ld:')) {
      keysToRemove.push(key);
    }
  }
  // Manter apenas as 2 entradas mais recentes
  if (keysToRemove.length > 2) {
    keysToRemove.slice(0, -2).forEach((key) => localStorage.removeItem(key));
  }
}

export function LDContextUpdater() {
  const ldClient = useLDClient();
  const { user, userData, userLoaded } = useAuthV2();

  // Limpa cache antigo do LaunchDarkly ao montar
  useEffect(() => {
    cleanOldLDEntries();
  }, []);

  useEffect(() => {
    if (!ldClient || !userLoaded) return;

    let cancelled = false;
    let unsubscribeReadyListener: (() => void) | null = null;
    const locale = typeof window !== 'undefined' ? navigator.language : 'pt-BR';
    const context = buildLaunchDarklyContext({
      user,
      userData,
      locale,
    });

    const syncContext = async () => {
      try {
        const identified = await identifyLaunchDarklyContext({
          ldClient,
          user,
          userData,
          locale,
        });

        if (identified || cancelled) return;

        unsubscribeReadyListener = subscribeToLaunchDarklyReady({
          ldClient,
          onReady: () => {
            if (cancelled) return;

            unsubscribeReadyListener?.();
            void Promise.resolve(ldClient.identify(context)).catch(() => undefined);
          },
        });
      } catch (error) {
        if (isLaunchDarklyInitializationTimeout(error)) {
          return;
        }
      }
    };

    void syncContext();

    return () => {
      cancelled = true;
      unsubscribeReadyListener?.();
    };
  }, [ldClient, user, userData, userLoaded]);

  return null; // Componente invisível
}
